import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Play, Lock, CheckCircle, Clock, Dumbbell, Heart, Target, Zap,
  ChevronRight, Star, TrendingUp, Unlock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Beta mode - all sessions unlocked
const BETA_MODE = false;

const TrainingSessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDifficulty, setSelectedDifficulty] = useState("beginner");
  const [completedSessions, setCompletedSessions] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSessions();
    loadCompletedSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/sessions`, { headers });
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedSessions = () => {
    const saved = localStorage.getItem('completedSessions');
    if (saved) {
      setCompletedSessions(JSON.parse(saved));
    }
  };

  const isSessionCompleted = (sessionId) => {
    return completedSessions.includes(sessionId);
  };

  const markSessionComplete = (sessionId) => {
    if (!completedSessions.includes(sessionId)) {
      const updated = [...completedSessions, sessionId];
      setCompletedSessions(updated);
      localStorage.setItem('completedSessions', JSON.stringify(updated));
    }
  };

  const startSession = (session) => {
    // Mark the session ID in localStorage so Engine Monitor can track it
    localStorage.setItem('currentSessionId', session.id);
    
    toast.success(`Starting ${session.name}!`, {
      description: `${session.drills.length} drills • ~${session.estimated_minutes} min`
    });
    
    navigate('/engine', { 
      state: { 
        sessionMode: true,
        session: session
      } 
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-400';
      case 'intermediate': return 'text-yellow-400';
      case 'advanced': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getDifficultyBg = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 border-green-500/50';
      case 'intermediate': return 'bg-yellow-500/20 border-yellow-500/50';
      case 'advanced': return 'bg-red-500/20 border-red-500/50';
      default: return 'bg-gray-500/20 border-gray-500/50';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'stamina': return <Heart className="w-5 h-5 text-red-500" />;
      case 'power': return <Zap className="w-5 h-5 text-yellow-500" />;
      case 'agility': return <Target className="w-5 h-5 text-blue-500" />;
      default: return <Dumbbell className="w-5 h-5 text-orange-500" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'stamina': return 'from-red-600/20 via-red-500/10 to-transparent border-red-500/30';
      case 'power': return 'from-yellow-600/20 via-yellow-500/10 to-transparent border-yellow-500/30';
      case 'agility': return 'from-blue-600/20 via-blue-500/10 to-transparent border-blue-500/30';
      default: return 'from-orange-600/20 via-orange-500/10 to-transparent border-orange-500/30';
    }
  };

  const difficulties = [
    { id: 'beginner', name: 'Beginner', shortName: 'Beginner', description: 'Foundation building', color: 'green' },
    { id: 'intermediate', name: 'Intermediate', shortName: 'Inter.', description: 'Increased intensity', color: 'yellow' },
    { id: 'advanced', name: 'Advanced', shortName: 'Advanced', description: 'Elite training', color: 'red' },
  ];

  const filteredSessions = sessions.filter(s => s.difficulty === selectedDifficulty);
  
  // Group by category
  const sessionsByCategory = {
    stamina: filteredSessions.filter(s => s.category === 'stamina'),
    power: filteredSessions.filter(s => s.category === 'power'),
    agility: filteredSessions.filter(s => s.category === 'agility'),
  };

  const getCompletionStats = (difficulty) => {
    const diffSessions = sessions.filter(s => s.difficulty === difficulty);
    const completed = diffSessions.filter(s => isSessionCompleted(s.id)).length;
    return { completed, total: diffSessions.length };
  };

  const stats = getCompletionStats(selectedDifficulty);

  // In beta mode, all difficulties are unlocked
  const isDifficultyUnlocked = (diffId) => {
    if (BETA_MODE) return true;
    if (diffId === 'beginner') return true;
    if (diffId === 'intermediate') {
      return getCompletionStats('beginner').completed >= 9;
    }
    if (diffId === 'advanced') {
      return getCompletionStats('intermediate').completed >= 12;
    }
    return false;
  };

  // In beta mode, all sessions are unlocked
  const isSessionUnlocked = (session, index) => {
    if (BETA_MODE) return true;
    if (index === 0) return true;
    // Previous session in same category must be completed
    return true; // Simplified for beta
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <div className="text-orange-500 text-lg font-heading font-bold uppercase">Loading Sessions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-4xl font-heading font-black text-white tracking-tight uppercase">
            Training Sessions
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Structured workouts that get progressively harder
          </p>
        </div>

        {/* Difficulty Tabs - Mobile Optimized */}
        <div className="flex gap-2" data-testid="difficulty-selector">
          {difficulties.map((diff) => {
            const isSelected = selectedDifficulty === diff.id;
            const diffStats = getCompletionStats(diff.id);
            const isUnlocked = isDifficultyUnlocked(diff.id);
            const percentage = diffStats.total > 0 ? Math.round((diffStats.completed / diffStats.total) * 100) : 0;
            
            return (
              <motion.button
                key={diff.id}
                onClick={() => isUnlocked && setSelectedDifficulty(diff.id)}
                disabled={!isUnlocked}
                whileTap={{ scale: 0.98 }}
                className={`flex-1 p-3 md:p-4 border-2 transition-all ${
                  isSelected 
                    ? getDifficultyBg(diff.id)
                    : isUnlocked
                      ? 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'
                      : 'border-zinc-800/50 bg-zinc-900/30 opacity-50 cursor-not-allowed'
                }`}
                data-testid={`difficulty-${diff.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm md:text-base font-bold ${isSelected ? getDifficultyColor(diff.id) : 'text-white'}`}>
                    <span className="hidden sm:inline">{diff.name}</span>
                    <span className="sm:hidden">{diff.shortName}</span>
                  </span>
                  {!isUnlocked && <Lock className="w-3 h-3 text-zinc-600" />}
                  {BETA_MODE && <Unlock className="w-3 h-3 text-cyan-500" />}
                </div>
                <p className="text-[10px] md:text-xs text-zinc-500 text-left hidden sm:block">{diff.description}</p>
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] md:text-xs mb-1">
                    <span className="text-zinc-500">{diffStats.completed}/{diffStats.total}</span>
                    <span className="text-zinc-500">{percentage}%</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-1">
                    <motion.div 
                      className={`h-1 rounded-full ${
                        diff.color === 'green' ? 'bg-green-500' :
                        diff.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Progress Banner */}
        <Card className={`bg-gradient-to-r ${getDifficultyBg(selectedDifficulty)} border`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white capitalize">{selectedDifficulty} Sessions</h3>
                <p className="text-sm text-zinc-400">
                  {stats.completed} of {stats.total} complete
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black text-white">
                  {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sessions by Category */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDifficulty}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {Object.entries(sessionsByCategory).map(([category, categorySessions]) => {
              if (categorySessions.length === 0) return null;
              
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    {getCategoryIcon(category)}
                    <h2 className="text-lg md:text-xl font-heading font-bold text-white capitalize">{category}</h2>
                    <Badge variant="outline" className="text-xs border-zinc-700">
                      {categorySessions.filter(s => isSessionCompleted(s.id)).length}/{categorySessions.length}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {categorySessions.map((session, index) => {
                      const completed = isSessionCompleted(session.id);
                      const unlocked = isSessionUnlocked(session, index);
                      
                      return (
                        <motion.div
                          key={session.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card 
                            className={`bg-gradient-to-r ${getCategoryColor(category)} border overflow-hidden cursor-pointer hover:border-orange-500/50 transition-all`}
                            onClick={() => unlocked && startSession(session)}
                            data-testid={`session-${session.id}`}
                          >
                            <CardContent className="p-0">
                              <div className="flex items-center">
                                {/* Status Icon */}
                                <div className={`w-14 md:w-16 flex items-center justify-center py-5 ${
                                  completed ? 'bg-green-500/20' : 'bg-zinc-900/50'
                                }`}>
                                  {completed ? (
                                    <CheckCircle className="w-6 h-6 text-green-500" />
                                  ) : unlocked ? (
                                    <Play className="w-6 h-6 text-orange-500" />
                                  ) : (
                                    <Lock className="w-5 h-5 text-zinc-600" />
                                  )}
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 p-3 md:p-4 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <h3 className="font-bold text-white text-sm md:text-base truncate">{session.name}</h3>
                                      <p className="text-xs md:text-sm text-zinc-400 mt-0.5 line-clamp-1">{session.description}</p>
                                      
                                      {/* Drills Preview */}
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {session.drills.slice(0, 3).map((drill, i) => (
                                          <Badge key={i} variant="outline" className="text-[10px] md:text-xs border-zinc-700 px-1.5 py-0">
                                            {drill}
                                          </Badge>
                                        ))}
                                        {session.drills.length > 3 && (
                                          <Badge variant="outline" className="text-[10px] md:text-xs border-zinc-700 px-1.5 py-0">
                                            +{session.drills.length - 3}
                                          </Badge>
                                        )}
                                      </div>
                                      
                                      {/* Stats */}
                                      <div className="flex items-center gap-3 mt-2 text-[10px] md:text-xs text-zinc-500">
                                        <span className="flex items-center gap-1">
                                          <Dumbbell className="w-3 h-3" />
                                          {session.drills.length} drills
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          ~{session.estimated_minutes} min
                                        </span>
                                      </div>
                                    </div>
                                    
                                    {/* Action Button */}
                                    <div className="flex-shrink-0">
                                      {completed ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => { e.stopPropagation(); startSession(session); }}
                                          className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs h-8"
                                        >
                                          Redo
                                        </Button>
                                      ) : unlocked ? (
                                        <Button
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); startSession(session); }}
                                          className="bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs h-8"
                                          data-testid={`start-session-${session.id}`}
                                        >
                                          <Play className="w-3 h-3 mr-1" />
                                          Start
                                        </Button>
                                      ) : (
                                        <Button size="sm" disabled variant="outline" className="text-xs h-8 opacity-50">
                                          <Lock className="w-3 h-3 mr-1" />
                                          Locked
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* Beta Notice */}
        {BETA_MODE && (
          <Card className="bg-cyan-500/10 border-cyan-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Unlock className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-cyan-400 text-sm">Beta Mode Active</h3>
                  <p className="text-xs text-zinc-400">All sessions unlocked for beta testers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progression Info */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading text-white">How Sessions Work</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-xs md:text-sm text-zinc-400">
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Beginner:</strong> Foundation building - learn proper form</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Intermediate:</strong> Increased intensity - more sets, less rest</span>
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Advanced:</strong> Elite training - maximum intensity</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrainingSessions;
